import { WithStyles } from '../../../../style';
import { BookingState, connectWithStyles } from '../../../../store';
import { FunctionalComponent, h } from 'preact';
import * as styles from '../Histogram/Histogram.scss';
import * as appStyles from '../../../../assets/global.scss';
import { ClientType, HistogramData, MonthPrice, SearchType } from '../../../../common';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Item } from './Item/Item';
import Icon from '../../../../common/Icon/Icon';
import { ExtensionApi } from '../../../../config';
import { throttle } from 'lodash';
import { ScreenResolution } from '../../../../common/hooks/ScreenResolution';
import { calculateHeight } from '../../../../utils/histogramUtils';

export interface HistogramProps {
  selectMonth: (selectedMonth: number) => void;
  selectedMonth: number;
  histogramData: HistogramData;
}

interface StoreProps {
  extensionApi: ExtensionApi;
}

enum ScrollDirections {
  LEFT = 'left',
  RIGHT = 'right',
}

const mapStateToProps = ({ extensionApi }: BookingState): StoreProps => ({
  extensionApi,
});

export type EffectiveProps = HistogramProps & WithStyles & StoreProps;

export const Histogram: FunctionalComponent<HistogramProps> = connectWithStyles<HistogramProps>([styles, appStyles], {
  mapStateToProps,
})(
  ({
    tx,
    selectMonth,
    selectedMonth,
    histogramData: { months = [], minPrice, maxPrice },
    extensionApi: { gtmService },
  }: EffectiveProps) => {
    const wrapperRef = useRef(null);
    const histogram = useRef(null);

    const [height, setHeight] = useState(0);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [clientType] = ScreenResolution();

    const MIN_COLUMN_HEIGHT = 44;
    const SCROLL_STEP = 120;

    useEffect(() => {
      setHeight(wrapperRef.current.clientHeight);

      histogram.current.addEventListener('scroll', onScrollThrottled);
      return function cleanup() {
        histogram.current.removeEventListener('scroll', onScrollThrottled);
      };
    }, []);

    useEffect(() => {
      const parent = histogram.current.getBoundingClientRect();
      const selected = histogram.current.querySelectorAll('.bw-selected').item(0)?.getBoundingClientRect();
      if (selected) {
        if (parent.x > selected.x) {
          scrollTo(ScrollDirections.LEFT);
        }

        if (parent.x + parent.width < selected.x + selected.width) {
          scrollTo(ScrollDirections.RIGHT);
        }
      }
    }, [selectedMonth]);

    const scrollTo = (direction: ScrollDirections): void => {
      requestAnimationFrame(() => {
        histogram.current.scrollBy({
          left: direction === ScrollDirections.LEFT ? -SCROLL_STEP : SCROLL_STEP,
          behavior: 'smooth',
        });
      });

      gtmService.trackElement({
        context: 'flight-search',
        elementType: 'BOOKING_WIDGET',
        elementName: 'histogram',
        state: 'arrow-click',
        searchType: SearchType.BOOKING_FLOW,
      });
    };

    const onScroll = (e): void => {
      e.preventDefault();
      e.stopPropagation();

      const scrollLeft = histogram.current?.scrollLeft;
      const contentWidth = histogram.current?.scrollWidth;
      const visibleWidth = histogram.current?.offsetWidth;

      setShowRightArrow(scrollLeft + visibleWidth < contentWidth - 20);
      setShowLeftArrow(scrollLeft !== 0);
    };

    const onScrollThrottled = throttle(onScroll, 300);

    const calculateColumnsHeight = (monthPrice: MonthPrice): string => {
      return calculateHeight(monthPrice, {
        minPrice,
        maxPrice,
        height,
        minHeight: MIN_COLUMN_HEIGHT,
      });
    };

    const onSelectMonth = (monthNumber) => {
      const { isCheapest, noFlight } = months.find((month, index) => index === monthNumber);

      selectMonth(monthNumber);

      if (!noFlight) {
        gtmService.trackElement({
          context: 'flight-search',
          elementType: 'BOOKING_WIDGET',
          elementName: 'histogram',
          state: isCheapest ? 'lowest-month-click' : 'other-month-click',
          searchType: SearchType.BOOKING_FLOW,
        });
      }
    };

    const arrowSizeClasses = (): { [id: string]: boolean } => ({
      'is-medium': clientType === ClientType.MOBILE_TABLET,
      'is-large': [ClientType.DESKTOP, ClientType.LAPTOP].includes(clientType),
    });

    return (
      <div className={tx('sticky')}>
        <div className={tx('histogram relative flex flex--column-reverse border-bottom')}>
          {showLeftArrow && (
            <Icon
              iconName="chevron-left"
              onClick={() => scrollTo(ScrollDirections.LEFT)}
              class={tx(
                'scroll-positions absolute left icon ps-small-l white-bg border-left nordic-blue-900-text circle border link-hover',
                arrowSizeClasses()
              )}
            />
          )}

          <div className={tx('items-wrapper')} ref={histogram}>
            <div className={tx('items-container flex flex--row')} ref={wrapperRef}>
              {months?.map((month, key) => (
                <Item
                  key={key}
                  monthData={month}
                  selectMonth={onSelectMonth}
                  monthId={key}
                  height={calculateColumnsHeight(month)}
                  isSelected={selectedMonth === key}
                  minHeight={`${MIN_COLUMN_HEIGHT}px`}
                />
              ))}
            </div>
          </div>
          {showRightArrow && (
            <Icon
              iconName="chevron-right"
              onClick={() => scrollTo(ScrollDirections.RIGHT)}
              class={tx(
                'scroll-positions absolute right icon ps-small-l white-bg nordic-blue-900-text circle border link-hover',
                arrowSizeClasses()
              )}
            />
          )}
        </div>
      </div>
    );
  }
);
